import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CalendarClock,
  CalendarPlus,
  Check,
  Clock3,
  Link2,
  Mail,
  Pencil,
  Phone,
  RefreshCw,
  Unplug,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import {
  Table,
  type TableColumn,
} from "../../../components/ui/Table";

type EstadoTarea = "pendiente" | "completada" | "cancelada";
type PeriodoAgenda = "" | "vencidas" | "hoy" | "proximas";
type TipoAgenda = "" | AgendaItem["tipo"];

interface AgendaItem {
  id: number;
  id_externo: string | null;
  crm_cliente_id: number;
  tipo: "tarea" | "llamada" | "reunion" | "email";
  empresa_id_externo: string | null;
  empresa: string;
  titulo: string;
  detalle: string | null;
  vence_at: string;
  recordatorio_at: string | null;
  estado: EstadoTarea;
  prioridad: "baja" | "normal" | "alta";
  contacto_id: number | null;
  contacto: string | null;
  contacto_telefono: string | null;
  contacto_email: string | null;
  oportunidad_id: number | null;
  oportunidad_id_externo: string | null;
  oportunidad: string | null;
  calendar_sync_estado: "no_solicitado" | "pendiente" | "sincronizado" | "error";
  calendar_sync_error: string | null;
  responsable_user_id: number | null;
  responsable_nombre: string | null;
  responsable_email: string | null;
}

interface AgendaResponse {
  data: AgendaItem[];
  canViewAllAgendas: boolean;
  selectedResponsible: "all" | number;
}

interface AgendaResponsible {
  id: number;
  nombre: string | null;
  email: string;
}

interface ContactoOption {
  id: number;
  nombre: string;
  activo: boolean;
  telefono: string | null;
  email: string | null;
}

interface OportunidadOption {
  id: number;
  idExterno: string | null;
  titulo: string;
  estado: "abierta" | "ganada" | "perdida";
}

interface SeguimientoEmpresaResponse {
  oportunidades: OportunidadOption[];
}

interface ContactosResponse {
  data: ContactoOption[];
}

interface TaskForm {
  tipo: AgendaItem["tipo"];
  titulo: string;
  detalle: string;
  venceAt: string;
  recordatorioAt: string;
  estado: EstadoTarea;
  prioridad: AgendaItem["prioridad"];
  contactoId: string;
  oportunidadId: string;
}

interface CalendarStatus {
  configured: boolean;
  redirectUri: string;
  connected: boolean;
  connection: null | {
    estado: "activa" | "error";
    calendarId: string;
    ultimoError: string | null;
    lastUsedAt: string | null;
    updatedAt: string;
  };
}

function getErrorMessage(
  error: unknown,
  fallback = "No se pudo completar la operación"
): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? fallback;
  }
  return fallback;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function localDayKey(value: Date): string {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
}

function toLocalDateTimeInput(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function CrmAgendaTab() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const agendaTableRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(null);
  const [estado, setEstado] = useState<EstadoTarea>(() => {
    const requested = searchParams.get("estado");
    return requested === "completada" || requested === "cancelada"
      ? requested
      : "pendiente";
  });
  const [selectedResponsible, setSelectedResponsible] = useState(
    () => searchParams.get("responsable_user_id") ?? "me"
  );
  const [periodo, setPeriodo] = useState<PeriodoAgenda>(() => {
    const requested = searchParams.get("periodo");
    return requested === "vencidas" || requested === "hoy" || requested === "proximas"
      ? requested
      : "";
  });
  const [tipo, setTipo] = useState<TipoAgenda>(() => {
    const requested = searchParams.get("tipo");
    return requested === "tarea" || requested === "llamada" || requested === "reunion" || requested === "email"
      ? requested
      : "";
  });
  const [responsibles, setResponsibles] = useState<AgendaResponsible[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [calendarAction, setCalendarAction] = useState<
    "connect" | "disconnect" | number | null
  >(null);
  const [editingTask, setEditingTask] = useState<AgendaItem | null>(null);
  const [taskForm, setTaskForm] = useState<TaskForm | null>(null);
  const [taskContacts, setTaskContacts] = useState<ContactoOption[]>([]);
  const [taskOpportunities, setTaskOpportunities] = useState<
    OportunidadOption[]
  >([]);
  const [taskOptionsLoading, setTaskOptionsLoading] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskError, setTaskError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [agendaResponse, statusResponse] = await Promise.all([
        api.get<AgendaResponse>("/admin/crm/seguimiento/agenda", {
          params: { estado, responsable_user_id: selectedResponsible },
        }),
        api.get<CalendarStatus>("/admin/crm/calendar/status"),
      ]);
      setItems(agendaResponse.data.data);
      setCalendarStatus(statusResponse.data);
    } catch (loadError) {
      setItems([]);
      setError(getErrorMessage(loadError, "No se pudo cargar la agenda"));
    } finally {
      setLoading(false);
    }
  }, [estado, selectedResponsible]);

  useEffect(() => {
    if (!user?.crmSupervisor) return;
    api
      .get<{ data: AgendaResponsible[] }>("/admin/crm/seguimiento/responsables-agenda")
      .then((response) => setResponsibles(response.data.data))
      .catch(() => setResponsibles([]));
  }, [user?.crmSupervisor]);

  useEffect(() => {
    void load();

    const refresh = () => void load();
    window.addEventListener("crm:seguimiento-actualizado", refresh);

    return () => {
      window.removeEventListener("crm:seguimiento-actualizado", refresh);
    };
  }, [load]);

  const counters = useMemo(() => {
    if (estado !== "pendiente") {
      return { overdue: 0, today: 0, upcoming: 0 };
    }

    const now = new Date();
    const today = localDayKey(now);
    return items.reduce(
      (result, item) => {
        const due = new Date(item.vence_at);
        const dueDay = localDayKey(due);
        if (due.getTime() < now.getTime() && dueDay !== today) result.overdue += 1;
        else if (dueDay === today) result.today += 1;
        else result.upcoming += 1;
        return result;
      },
      { overdue: 0, today: 0, upcoming: 0 }
    );
  }, [estado, items]);

  const filteredItems = useMemo(() => {
    const now = new Date();
    const today = localDayKey(now);
    return items
      .filter((item) => {
        if (tipo && item.tipo !== tipo) return false;
        const dueDay = localDayKey(new Date(item.vence_at));
        if (periodo === "vencidas") return dueDay < today;
        if (periodo === "hoy") return dueDay === today;
        if (periodo === "proximas") return dueDay > today;
        return true;
      })
      .sort((left, right) => {
        const dateDifference =
          new Date(left.vence_at).getTime() - new Date(right.vence_at).getTime();
        return dateDifference || left.id - right.id;
      });
  }, [items, periodo, tipo]);

  const applyPeriodFilter = (nextPeriod: Exclude<PeriodoAgenda, "">) => {
    setPeriodo(nextPeriod);
    window.setTimeout(() => {
      agendaTableRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const openTask = async (item: AgendaItem) => {
    setEditingTask(item);
    setTaskForm({
      tipo: item.tipo,
      titulo: item.titulo,
      detalle: item.detalle ?? "",
      venceAt: toLocalDateTimeInput(item.vence_at),
      recordatorioAt: toLocalDateTimeInput(item.recordatorio_at),
      estado: item.estado,
      prioridad: item.prioridad,
      contactoId: item.contacto_id ? String(item.contacto_id) : "",
      oportunidadId: item.oportunidad_id ? String(item.oportunidad_id) : "",
    });
    setTaskContacts([]);
    setTaskOpportunities([]);
    setTaskError("");
    setTaskOptionsLoading(true);
    try {
      const [contactsResponse, trackingResponse] = await Promise.all([
        api.get<ContactosResponse>(
          `/admin/crm/contactos/empresa/${item.crm_cliente_id}`
        ),
        api.get<SeguimientoEmpresaResponse>(
          `/admin/crm/seguimiento/empresa/${item.crm_cliente_id}`
        ),
      ]);
      setTaskContacts(contactsResponse.data.data);
      setTaskOpportunities(trackingResponse.data.oportunidades);
    } catch (optionsError) {
      setTaskError(
        getErrorMessage(
          optionsError,
          "No se pudieron cargar los contactos y oportunidades relacionados"
        )
      );
    } finally {
      setTaskOptionsLoading(false);
    }
  };

  const closeTask = () => {
    if (taskSaving) return;
    setEditingTask(null);
    setTaskForm(null);
    setTaskError("");
  };

  const saveTask = async () => {
    if (!editingTask || !taskForm) return;
    setTaskError("");
    if (!taskForm.titulo.trim() || !taskForm.venceAt) {
      setTaskError("Indica el título y la fecha de la próxima acción.");
      return;
    }

    setTaskSaving(true);
    try {
      await api.patch(`/admin/crm/seguimiento/tareas/${editingTask.id}`, {
        tipo: taskForm.tipo,
        titulo: taskForm.titulo.trim(),
        detalle: taskForm.detalle.trim() || null,
        vence_at: new Date(taskForm.venceAt).toISOString(),
        recordatorio_at: taskForm.recordatorioAt
          ? new Date(taskForm.recordatorioAt).toISOString()
          : null,
        estado: taskForm.estado,
        prioridad: taskForm.prioridad,
        contacto_id: taskForm.contactoId
          ? Number(taskForm.contactoId)
          : null,
        oportunidad_id: taskForm.oportunidadId
          ? Number(taskForm.oportunidadId)
          : null,
      });
      setEditingTask(null);
      setTaskForm(null);
      setTaskError("");
      await load();
      window.dispatchEvent(new Event("crm:seguimiento-actualizado"));
    } catch (saveError) {
      setTaskError(getErrorMessage(saveError, "No se pudo guardar la tarea"));
    } finally {
      setTaskSaving(false);
    }
  };

  const changeState = async (item: AgendaItem, nextState: EstadoTarea) => {
    setUpdatingId(item.id);
    setError("");
    try {
      await api.patch(`/admin/crm/seguimiento/tareas/${item.id}`, {
        estado: nextState,
      });
      await load();
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setUpdatingId(null);
    }
  };

  const connectCalendar = async () => {
    setCalendarAction("connect");
    setError("");
    try {
      const response = await api.post<{ authorizationUrl: string }>(
        "/admin/crm/calendar/connect"
      );
      window.location.assign(response.data.authorizationUrl);
    } catch (connectError) {
      setError(getErrorMessage(connectError, "No se pudo iniciar la conexión con Google"));
      setCalendarAction(null);
    }
  };

  const disconnectCalendar = async () => {
    setCalendarAction("disconnect");
    setError("");
    try {
      await api.delete("/admin/crm/calendar/connection");
      await load();
    } catch (disconnectError) {
      setError(
        getErrorMessage(disconnectError, "No se pudo desconectar Google Calendar")
      );
    } finally {
      setCalendarAction(null);
    }
  };

  const syncCalendar = async (item: AgendaItem) => {
    setCalendarAction(item.id);
    setError("");
    try {
      await api.post(`/admin/crm/calendar/tasks/${item.id}/sync`);
      await load();
    } catch (syncError) {
      setError(getErrorMessage(syncError, "No se pudo sincronizar la tarea"));
      await load();
    } finally {
      setCalendarAction(null);
    }
  };

  const oauthMessage = (() => {
    switch (searchParams.get("calendar")) {
      case "connected":
        return { ok: true, text: "Google Calendar se ha conectado correctamente." };
      case "cancelled":
        return { ok: false, text: "La conexión con Google Calendar se canceló." };
      case "expired":
        return { ok: false, text: "La solicitud de conexión caducó. Inténtalo de nuevo." };
      case "not_configured":
        return { ok: false, text: "Google Calendar todavía no está configurado." };
      case "error":
        return { ok: false, text: "Google no pudo completar la conexión." };
      default:
        return null;
    }
  })();

  const columns: TableColumn<AgendaItem>[] = [
    {
      key: "vence_at",
      header: "Fecha",
      render: (item) => (
        <div className="whitespace-nowrap font-medium">
          {formatDateTime(item.vence_at)}
        </div>
      ),
    },
    {
      key: "empresa",
      header: "Empresa",
      render: (item) => (
        <div>
          <div className="font-medium">{item.empresa}</div>
          <div className="text-xs text-gray-500">
            {item.empresa_id_externo ?? "Prospect sin ID de cliente"}
          </div>
        </div>
      ),
    },
    {
      key: "titulo",
      header: "Próxima acción",
      render: (item) => (
        <div className="min-w-[240px]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral">
              {item.tipo === "llamada"
                ? "Llamada"
                : item.tipo === "reunion"
                  ? "Reunión"
                  : item.tipo === "email"
                    ? "Email"
                    : "Tarea"}
            </Badge>
            <span className="font-medium">{item.titulo}</span>
          </div>
          {item.detalle && (
            <div className="mt-1 text-xs text-gray-500">{item.detalle}</div>
          )}
        </div>
      ),
    },
    {
      key: "contacto",
      header: "Contacto",
      render: (item) => (
        <div className="min-w-[190px] space-y-1">
          <div className="font-medium text-gray-900">
            {item.contacto || "Contacto no informado"}
          </div>
          {item.contacto_telefono ? (
            <a
              href={`tel:${item.contacto_telefono}`}
              onClick={(event) => event.stopPropagation()}
              className="flex items-center gap-1.5 text-xs text-[#1A3AB8] hover:underline"
            >
              <Phone className="h-3.5 w-3.5" />
              {item.contacto_telefono}
            </a>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Phone className="h-3.5 w-3.5" /> Teléfono no informado
            </div>
          )}
          {item.contacto_email ? (
            <a
              href={`mailto:${item.contacto_email}`}
              onClick={(event) => event.stopPropagation()}
              className="flex items-center gap-1.5 text-xs text-[#1A3AB8] hover:underline"
            >
              <Mail className="h-3.5 w-3.5" />
              {item.contacto_email}
            </a>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Mail className="h-3.5 w-3.5" /> Email no informado
            </div>
          )}
        </div>
      ),
    },
    {
      key: "oportunidad",
      header: "Oportunidad",
      render: (item) =>
        item.oportunidad ? (
          <div>
            <div>{item.oportunidad}</div>
            <div className="text-xs text-gray-500">
              {item.oportunidad_id_externo}
            </div>
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "prioridad",
      header: "Prioridad",
      render: (item) => {
        if (item.prioridad === "alta") return <Badge variant="danger">Alta</Badge>;
        if (item.prioridad === "baja") return <Badge variant="neutral">Baja</Badge>;
        return <Badge variant="info">Normal</Badge>;
      },
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (item) => (
        <div
          className="flex min-w-[180px] flex-col items-start gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={updatingId !== null || calendarAction !== null}
            onClick={() => void openTask(item)}
          >
            <Pencil className="h-4 w-4" /> Abrir / editar
          </Button>
          {calendarStatus?.connected && item.responsable_user_id === user?.id && (
            <Button
              size="sm"
              variant="secondary"
              loading={calendarAction === item.id}
              disabled={calendarAction !== null || updatingId !== null}
              onClick={() => syncCalendar(item)}
            >
              {item.calendar_sync_estado === "sincronizado" ? (
                <RefreshCw className="h-4 w-4" />
              ) : (
                <CalendarPlus className="h-4 w-4" />
              )}
              {item.calendar_sync_estado === "sincronizado"
                ? "Actualizar calendario"
                : item.calendar_sync_estado === "error"
                  ? "Reintentar calendario"
                  : "Añadir al calendario"}
            </Button>
          )}
          {item.calendar_sync_estado === "sincronizado" && (
            <Badge variant="success">En Google Calendar</Badge>
          )}
          {item.calendar_sync_estado === "pendiente" && (
            <Badge variant="warning">Sincronizando</Badge>
          )}
          {item.calendar_sync_estado === "error" && (
            <span title={item.calendar_sync_error ?? undefined}>
              <Badge variant="danger">Error de calendario</Badge>
            </span>
          )}
          {item.estado === "pendiente" ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={updatingId !== null || calendarAction !== null}
              onClick={() => changeState(item, "completada")}
            >
              <Check className="h-4 w-4" /> Completar
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={updatingId !== null || calendarAction !== null}
              onClick={() => changeState(item, "pendiente")}
            >
              Reabrir
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (user?.crmSupervisor) {
    columns.splice(5, 0, {
      key: "responsable",
      header: "Responsable",
      render: (item) => item.responsable_nombre || item.responsable_email || "—",
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Agenda comercial</h2>
          <p className="mt-1 text-sm text-gray-600">
            Próximas acciones de todas las empresas y oportunidades.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          {user?.crmSupervisor && (
            <label className="block text-sm text-gray-700">
              <span className="mb-1 block font-medium">Agenda de</span>
              <select
                value={selectedResponsible}
                onChange={(event) => setSelectedResponsible(event.target.value)}
                className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="me">Mi agenda</option>
                <option value="all">Todas las agendas</option>
                {responsibles
                  .filter((responsible) => responsible.id !== user.id)
                  .map((responsible) => (
                    <option key={responsible.id} value={responsible.id}>
                      {responsible.nombre || responsible.email}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block font-medium">Estado</span>
            <select
              aria-label="Estado de las tareas"
              value={estado}
              onChange={(event) => setEstado(event.target.value as EstadoTarea)}
              className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="pendiente">Pendientes</option>
              <option value="completada">Completadas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </label>
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block font-medium">Periodo</span>
            <select
              aria-label="Periodo de las tareas"
              value={periodo}
              onChange={(event) => setPeriodo(event.target.value as PeriodoAgenda)}
              className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Cualquier fecha</option>
              <option value="vencidas">Vencidas</option>
              <option value="hoy">Para hoy</option>
              <option value="proximas">Próximas</option>
            </select>
          </label>
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block font-medium">Tipo</span>
            <select
              aria-label="Tipo de tarea"
              value={tipo}
              onChange={(event) => setTipo(event.target.value as TipoAgenda)}
              className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todos los tipos</option>
              <option value="tarea">Tareas</option>
              <option value="llamada">Llamadas</option>
              <option value="reunion">Reuniones</option>
              <option value="email">Emails</option>
            </select>
          </label>
        </div>
      </div>

      {oauthMessage && (
        <div
          className={`rounded-lg border p-4 text-sm ${
            oauthMessage.ok
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
        >
          {oauthMessage.text}
        </div>
      )}

      {calendarStatus && (
        <div
          className={`rounded-lg border p-4 ${
            calendarStatus.connected
              ? "border-green-200 bg-green-50"
              : calendarStatus.configured
                ? "border-blue-200 bg-blue-50"
                : "border-amber-200 bg-amber-50"
          }`}
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 font-medium text-gray-900">
                <Link2 className="h-5 w-5" /> Google Calendar
                {calendarStatus.connected && <Badge variant="success">Conectado</Badge>}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {calendarStatus.connected
                  ? "Puedes añadir y actualizar tus tareas en tu calendario personal."
                  : calendarStatus.configured
                    ? "Conecta tu cuenta para llevar las próximas acciones a tu calendario."
                    : "La integración está preparada, pero faltan las credenciales de Google Cloud."}
              </p>
              {!calendarStatus.configured && (
                <p className="mt-2 break-all font-mono text-xs text-gray-600">
                  URL de retorno: {calendarStatus.redirectUri}
                </p>
              )}
              {calendarStatus.connection?.ultimoError && (
                <p className="mt-2 text-xs text-red-700">
                  Último error: {calendarStatus.connection.ultimoError}
                </p>
              )}
            </div>
            {calendarStatus.connected ? (
              <Button
                variant="secondary"
                loading={calendarAction === "disconnect"}
                disabled={calendarAction !== null}
                onClick={disconnectCalendar}
              >
                <Unplug className="h-4 w-4" /> Desconectar
              </Button>
            ) : (
              <Button
                loading={calendarAction === "connect"}
                disabled={!calendarStatus.configured || calendarAction !== null}
                onClick={connectCalendar}
              >
                <Link2 className="h-4 w-4" /> Conectar Google Calendar
              </Button>
            )}
          </div>
        </div>
      )}

      {estado === "pendiente" && (
        <div className="grid gap-3 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => applyPeriodFilter("vencidas")}
            aria-pressed={periodo === "vencidas"}
            className={`rounded-lg border border-red-200 bg-red-50 p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
              periodo === "vencidas" ? "ring-2 ring-red-400" : ""
            }`}
          >
            <div className="flex items-center gap-2 text-red-800">
              <Clock3 className="h-5 w-5" />
              <span className="text-2xl font-bold">{counters.overdue}</span>
            </div>
            <p className="mt-1 text-sm text-red-700">Vencidas</p>
          </button>
          <button
            type="button"
            onClick={() => applyPeriodFilter("hoy")}
            aria-pressed={periodo === "hoy"}
            className={`rounded-lg border border-blue-200 bg-blue-50 p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
              periodo === "hoy" ? "ring-2 ring-blue-400" : ""
            }`}
          >
            <div className="flex items-center gap-2 text-blue-800">
              <CalendarClock className="h-5 w-5" />
              <span className="text-2xl font-bold">{counters.today}</span>
            </div>
            <p className="mt-1 text-sm text-blue-700">Para hoy</p>
          </button>
          <button
            type="button"
            onClick={() => applyPeriodFilter("proximas")}
            aria-pressed={periodo === "proximas"}
            className={`rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 ${
              periodo === "proximas" ? "ring-2 ring-gray-400" : ""
            }`}
          >
            <div className="flex items-center gap-2 text-gray-800">
              <CalendarClock className="h-5 w-5" />
              <span className="text-2xl font-bold">{counters.upcoming}</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">Próximas</p>
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div ref={agendaTableRef} className="scroll-mt-4 space-y-3">
        <div className="text-sm text-gray-600">
          {filteredItems.length === 1 ? "1 acción" : `${filteredItems.length} acciones`}
        </div>

        <Table
          columns={columns}
          data={filteredItems}
          loading={loading}
          emptyMessage="No hay acciones en este estado"
          floatingHorizontalScrollbar
          onRowClick={(item) => void openTask(item)}
        />
      </div>

      <Modal
        isOpen={editingTask !== null}
        onClose={closeTask}
        title={editingTask ? `Tarea ${editingTask.id_externo ?? ""}`.trim() : "Tarea"}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeTask}
              disabled={taskSaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={saveTask}
              loading={taskSaving}
              disabled={!taskForm || taskOptionsLoading}
            >
              Guardar cambios
            </Button>
          </>
        }
      >
        {editingTask && taskForm && (
          <div className="space-y-5">
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
              <div className="font-medium">{editingTask.empresa}</div>
              <div className="mt-1 text-blue-700">
                Responsable: {editingTask.responsable_nombre || editingTask.responsable_email || "Sin asignar"}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-blue-800">
                <span className="font-medium">
                  {editingTask.contacto || "Contacto no informado"}
                </span>
                {editingTask.contacto_telefono ? (
                  <a
                    href={`tel:${editingTask.contacto_telefono}`}
                    className="inline-flex items-center gap-1.5 hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {editingTask.contacto_telefono}
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-blue-500">
                    <Phone className="h-4 w-4" /> Teléfono no informado
                  </span>
                )}
                {editingTask.contacto_email ? (
                  <a
                    href={`mailto:${editingTask.contacto_email}`}
                    className="inline-flex items-center gap-1.5 hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    {editingTask.contacto_email}
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-blue-500">
                    <Mail className="h-4 w-4" /> Email no informado
                  </span>
                )}
              </div>
            </div>

            {taskError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {taskError}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Tipo de próxima acción
                </span>
                <select
                  value={taskForm.tipo}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current
                        ? {
                            ...current,
                            tipo: event.target.value as AgendaItem["tipo"],
                          }
                        : current
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="tarea">Tarea general</option>
                  <option value="llamada">Llamada</option>
                  <option value="reunion">Reunión</option>
                  <option value="email">Email</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Estado
                </span>
                <select
                  value={taskForm.estado}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current
                        ? {
                            ...current,
                            estado: event.target.value as EstadoTarea,
                          }
                        : current
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="completada">Completada</option>
                  <option value="cancelada">Descartada</option>
                </select>
              </label>
              <div className="md:col-span-2">
                <Input
                  label="Próxima acción *"
                  value={taskForm.titulo}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current ? { ...current, titulo: event.target.value } : current
                    )
                  }
                />
              </div>
              <Input
                label="Fecha y hora *"
                type="datetime-local"
                value={taskForm.venceAt}
                onChange={(event) =>
                  setTaskForm((current) =>
                    current ? { ...current, venceAt: event.target.value } : current
                  )
                }
              />
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Prioridad
                </span>
                <select
                  value={taskForm.prioridad}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current
                        ? {
                            ...current,
                            prioridad: event.target.value as AgendaItem["prioridad"],
                          }
                        : current
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="baja">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                </select>
              </label>
              <Input
                label="Aviso previo (opcional)"
                type="datetime-local"
                value={taskForm.recordatorioAt}
                onChange={(event) =>
                  setTaskForm((current) =>
                    current
                      ? { ...current, recordatorioAt: event.target.value }
                      : current
                  )
                }
              />
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Contacto relacionado
                </span>
                <select
                  value={taskForm.contactoId}
                  disabled={taskOptionsLoading}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current
                        ? { ...current, contactoId: event.target.value }
                        : current
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Sin contacto concreto</option>
                  {taskContacts
                    .filter(
                      (contact) =>
                        contact.activo || String(contact.id) === taskForm.contactoId
                    )
                    .map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.nombre}
                      </option>
                    ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Oportunidad relacionada
                </span>
                <select
                  value={taskForm.oportunidadId}
                  disabled={taskOptionsLoading}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current
                        ? { ...current, oportunidadId: event.target.value }
                        : current
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Sin oportunidad concreta</option>
                  {taskOpportunities.map((opportunity) => (
                    <option key={opportunity.id} value={opportunity.id}>
                      {opportunity.idExterno
                        ? `${opportunity.idExterno} · `
                        : ""}
                      {opportunity.titulo}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Detalle
                </span>
                <textarea
                  value={taskForm.detalle}
                  onChange={(event) =>
                    setTaskForm((current) =>
                      current ? { ...current, detalle: event.target.value } : current
                    )
                  }
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
                />
              </label>
            </div>

            {editingTask.calendar_sync_estado === "sincronizado" && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Después de guardar cambios de fecha, título o detalle, pulsa
                «Actualizar calendario» en la agenda para reflejarlos también en
                Google Calendar.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
